/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';

/**
 * AnnotationThumbnail
 * -------------------
 * Renders an 80x60 canvas preview of ONE annotation, ShotGrid-style.
 *
 * Behaviour:
 *   - For image annotations → draws the source image and overlays the
 *     annotation shape (rect / arrow / freehand / pin) in the ORIGINAL color.
 *   - For video annotations → seeks a hidden <video> element to
 *     annotation.timecode_sec, grabs that frame, then draws the shape.
 *   - Cropping: if the annotation has a bounding-box shape (rect / arrow /
 *     freehand), the canvas is cropped to that bbox expanded by 12% padding.
 *     Otherwise the whole frame is shown.
 *   - All shape coordinates are NORMALIZED 0..1 of native media box.
 */
export default function AnnotationThumbnail({ src, type = 'image', ann, size = { w: 96, h: 60 }, onClick }) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!src || !ann) return;
    let cancelled = false;

    const paint = (source, srcW, srcH) => {
      if (cancelled) return;
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      c.width = size.w; c.height = size.h;
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, c.width, c.height);

      // Compute crop bbox in source-pixel space from normalized shapes
      const bbox = computeBBox(ann, srcW, srcH);
      // Expand bbox by padding, clamp to source
      const pad = Math.max(srcW, srcH) * 0.06;
      const sx = Math.max(0, bbox.x - pad);
      const sy = Math.max(0, bbox.y - pad);
      const sw = Math.min(srcW - sx, bbox.w + pad * 2);
      const sh = Math.min(srcH - sy, bbox.h + pad * 2);

      // Letterbox into 80x60 preserving aspect
      const targetAR = size.w / size.h;
      const srcAR = sw / sh;
      let drawW = size.w, drawH = size.h, ox = 0, oy = 0;
      if (srcAR > targetAR) { drawH = size.w / srcAR; oy = (size.h - drawH) / 2; }
      else { drawW = size.h * srcAR; ox = (size.w - drawW) / 2; }

      try {
        ctx.drawImage(source, sx, sy, sw, sh, ox, oy, drawW, drawH);
      } catch (e) { /* may throw on tainted canvas — ignore */ }

      // Overlay shapes (scale from source→crop→canvas coords)
      const toC = (nx, ny) => {
        const px = nx * srcW, py = ny * srcH;
        return {
          x: ox + ((px - sx) / sw) * drawW,
          y: oy + ((py - sy) / sh) * drawH,
        };
      };
      ctx.lineWidth = 1.5;
      (ann.shapes || []).forEach(s => {
        ctx.strokeStyle = s.color || '#fb7185';
        ctx.setLineDash([]);
        if (s.kind === 'rect') {
          const a = toC(Math.min(s.x1, s.x2), Math.min(s.y1, s.y2));
          const b = toC(Math.max(s.x1, s.x2), Math.max(s.y1, s.y2));
          ctx.setLineDash([3, 2]);
          ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
        } else if (s.kind === 'arrow') {
          const a = toC(s.x1, s.y1); const b = toC(s.x2, s.y2);
          drawArrow(ctx, a.x, a.y, b.x, b.y);
        } else if (s.kind === 'freehand' && s.points?.length) {
          ctx.beginPath();
          s.points.forEach((p, i) => {
            const q = toC(p.x, p.y);
            if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
          });
          ctx.stroke();
        }
      });
      if (ann.point) {
        const p = toC(ann.point.x, ann.point.y);
        ctx.fillStyle = '#fb7185';
        ctx.beginPath(); ctx.arc(p.x, p.y, 3.5, 0, 2 * Math.PI); ctx.fill();
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.stroke();
      }
    };

    if (type === 'video') {
      const v = document.createElement('video');
      v.crossOrigin = 'anonymous';
      v.muted = true;
      v.playsInline = true;
      v.preload = 'auto';
      v.src = src;
      videoRef.current = v;
      const onLoaded = () => {
        if (cancelled) return;
        const t = ann.timecode_sec != null ? ann.timecode_sec : 0;
        try { v.currentTime = Math.min(t, (v.duration || 0) - 0.05); }
        catch { paint(v, v.videoWidth || 960, v.videoHeight || 540); }
      };
      const onSeeked = () => paint(v, v.videoWidth || 960, v.videoHeight || 540);
      v.addEventListener('loadedmetadata', onLoaded, { once: true });
      v.addEventListener('seeked', onSeeked, { once: true });
      // Some browsers need a manual kick
      v.load?.();
    } else {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => paint(img, img.naturalWidth || 960, img.naturalHeight || 540);
      img.onerror = () => {
        // Still render shapes on a placeholder background
        const fake = { naturalWidth: 960, naturalHeight: 540 };
        paint(new Image(), fake.naturalWidth, fake.naturalHeight);
      };
      img.src = src;
      imgRef.current = img;
    }

    return () => {
      cancelled = true;
      if (videoRef.current) { try { videoRef.current.src = ''; } catch { /* noop */ } }
    };
  }, [src, type, ann?.id, ann?.timecode_sec, JSON.stringify(ann?.shapes || []), JSON.stringify(ann?.point || null)]);

  return (
    <button
      type="button"
      onClick={onClick}
      title={ann?.timecode_sec != null ? `Jump to @ ${fmtTc(ann.timecode_sec)}` : 'Jump to annotation'}
      data-testid={`ann-thumb-${ann?.id}`}
      className="relative shrink-0 rounded-md overflow-hidden border border-zinc-700 hover:border-amber-400 bg-zinc-900 transition-colors cursor-pointer group"
      style={{ width: size.w, height: size.h }}
    >
      <canvas ref={canvasRef} width={size.w} height={size.h} className="block w-full h-full" />
      {ann?.timecode_sec != null && (
        <span className="absolute bottom-0.5 right-0.5 text-[9px] font-mono bg-black/70 text-amber-300 px-1 rounded">
          {fmtTc(ann.timecode_sec)}
        </span>
      )}
    </button>
  );
}

function computeBBox(ann, srcW, srcH) {
  const pts = [];
  (ann.shapes || []).forEach(s => {
    if (s.kind === 'rect' || s.kind === 'arrow') {
      pts.push({ x: s.x1 * srcW, y: s.y1 * srcH });
      pts.push({ x: s.x2 * srcW, y: s.y2 * srcH });
    } else if (s.kind === 'freehand' && s.points?.length) {
      s.points.forEach(p => pts.push({ x: p.x * srcW, y: p.y * srcH }));
    }
  });
  if (ann.point) pts.push({ x: ann.point.x * srcW, y: ann.point.y * srcH });
  if (pts.length === 0) {
    // No shape — return whole frame
    return { x: 0, y: 0, w: srcW, h: srcH };
  }
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  // Minimum size so pins aren't 0x0
  const w = Math.max(60, maxX - minX);
  const h = Math.max(60, maxY - minY);
  return { x: minX, y: minY, w, h };
}

function drawArrow(ctx, x1, y1, x2, y2) {
  ctx.beginPath();
  ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = 6;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
  ctx.stroke();
}

function fmtTc(t) {
  const m = Math.floor(t / 60).toString().padStart(2, '0');
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
